import React, {FC} from 'react';
import classNames from 'classnames';
import {secondsPer} from 'components/ContentPagesComponents/Charts/timing';
import {Markdown} from 'components/Markdown';
import {COLORS} from 'constants/index';
import {motion} from 'framer-motion';
import {useIsMobile} from 'hooks/useIsMobile';

import {useShuffle} from './useShuffle';

import * as styles from './styles.module.scss';

type ChartDotsMatrixProps = {
	completed: string
	rows: string
	columns: string
	description: string
	shuffle?: string
}

const ChartDotsMatrix: FC<ChartDotsMatrixProps> = ({rows = '4', columns = '5', description, completed, shuffle}) => {
	const parsed: Array<boolean[]> = [];

	// Create helper functions that parse our inputs
	const r = Number(rows);
	const c = Number(columns);
	const v = Number(completed);
	const n = (i: number, j: number) => i * c + j;

	// Set up a shuffle if we wanting to use it
	const s = useShuffle(v, r * c);
	const mobile = useIsMobile();

	// Create array from which to render our elements
	for (let i = 0; i < r; i++) {
		parsed.push([]);
		for (let j = 0; j < c; j++) {
			if (shuffle) {
				parsed[i].push(s[n(i, j)]);
			} else {
				parsed[i].push(n(i, j) < v);
			}
		}
	}

	// Set up a container with variants that stagger for us
	const containerProps = {
		className: styles.chart,
		initial: 'hidden',
		whileInView: 'show',
		viewport: {once: !mobile},

		variants: {
			show: {
				transition: {
					delayChildren: secondsPer(v) / 2,
					staggerChildren: secondsPer(v),
				}
			}
		}
	};

	// Create start and end color points for our elements
	const itemProps = (active: boolean) => ({
		className: classNames({
			[styles.item]: true,
			[styles.active]: active,
		}),
		...(!active ? {} : {
			variants: {
				hidden: {
					backgroundColor: COLORS.GRAY6,
				},
				show: {
					backgroundColor: COLORS.SALAD100,
				},
			}
		}),
	});

	// Put it all together to render our animated dot matrix
	return (
		<div className={styles.wrapper}>
			<motion.div {...containerProps}>
				{parsed.map((row, i) => (
					<div className={styles.row} key={i}>
						{row.map((col, j) => (
							<motion.div
								{...itemProps(col)}
								key={n(i, j)}
							/>
						))}
					</div>
				))}
			</motion.div>
			<div>
				<Markdown markdown={description} />
			</div>
		</div>
	);
};

export default ChartDotsMatrix;
